import React, { useState, useEffect, useRef } from 'react';
import termsOfUseApi from '../../services/termsOfUseApi';
import { usePermissions } from '../../hooks/usePermissions';

const TermsOfUseEditor = () => {
  const permissions = usePermissions('Terms of Use');
  const [mainHeading, setMainHeading] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const lastSectionRef = useRef(null);

  useEffect(() => {
    loadTermsOfUse();
  }, []);

  useEffect(() => {
    // Auto-scroll to the last section when sections change
    if (lastSectionRef.current) {
      lastSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [sections.length]);

  const loadTermsOfUse = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await termsOfUseApi.getTermsOfUse();
      if (result.success) {
        if (result.data) {
          setMainHeading(result.data.mainHeading || '');
          setSections(result.data.sections || []);
          setLastSaved(new Date(result.data.lastUpdated));
        } else {
          setMainHeading('');
          setSections([]);
          setLastSaved(null);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to load terms of use");
    } finally {
      setLoading(false);
    }
  };

  const saveTermsOfUse = async () => {
    try {
      setLoading(true);
      setError("");

      if (!mainHeading.trim()) {
        setError("Main heading is required");
        return;
      }

      if (sections.length === 0) {
        setError("At least one section is required");
        return;
      }

      const result = await termsOfUseApi.saveTermsOfUse(mainHeading, sections);
      if (result.success) {
        setSaved(true);
        setLastSaved(new Date(result.data.lastUpdated));
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to save terms of use");
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    setSections(prevSections => ([
      ...prevSections,
      {
        heading: '',
        content: ''
      }
    ]));
  };

  const updateSection = (index, field, value) => {
    const newSections = [...sections];
    newSections[index][field] = value;
    setSections(newSections);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const renderPreview = () => {
    return (
      <div className="prose prose-sm max-w-none">
        <h1 className="text-2xl font-bold mb-6">{mainHeading || 'Terms of Use'}</h1>
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            <h2 className="text-xl font-semibold mb-4">{section.heading || 'Section Heading'}</h2>
            <p className="whitespace-pre-wrap">{section.content || 'Section content'}</p>
          </div>
        ))}
      </div>
    );
  };

  // Centered Saved Popup
  const SavedPopup = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="bg-green-500 text-white px-8 py-4 rounded-lg shadow-lg text-lg font-semibold pointer-events-auto animate-fade-in">
        ✓ Terms of Use saved successfully!
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Terms of Use Editor
          </h1>
          <p className="text-gray-600">
            Create and edit your terms of use using the structured form below.
          </p>
          {lastSaved && (
            <p className="text-sm text-gray-500 mt-2">
              Last saved: {lastSaved.toLocaleString()}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {saved && <SavedPopup />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Form Editor</h2>
              <p className="text-sm text-gray-600">
                Fill in the details for your terms of use
              </p>
            </div>
            <div className="p-6 space-y-6">
              {/* Main Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Heading *
                </label>
                <input
                  type="text"
                  value={mainHeading}
                  onChange={(e) => setMainHeading(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter main heading"
                />
              </div>

              {/* Sections */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-medium text-gray-900">Sections</h3>
                  <button
                    onClick={addSection}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Add Section
                  </button>
                </div>

                {sections.map((section, sectionIndex) => (
                  <div
                    key={sectionIndex}
                    className="border border-gray-200 rounded-lg p-4 mb-4"
                    ref={sectionIndex === sections.length - 1 ? lastSectionRef : null}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Section {sectionIndex + 1}</h4>
                      <button
                        onClick={() => removeSection(sectionIndex)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    <input
                      type="text"
                      value={section.heading}
                      onChange={(e) => updateSection(sectionIndex, 'heading', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                      placeholder="Section heading"
                    />

                    <textarea
                      value={section.content}
                      onChange={(e) => updateSection(sectionIndex, 'content', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Section content"
                      rows="6"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Live Preview</h2>
              <p className="text-sm text-gray-600">
                See how your terms of use will appear
              </p>
            </div>
            <div className="p-6">
              {renderPreview()}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={saveTermsOfUse}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={loadTermsOfUse}
                disabled={loading}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Reload
              </button>
            </div>
            <div className="text-sm text-gray-500">
              {loading && 'Processing...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUseEditor;